import { FaFacebook, FaInstagram, FaYoutube, FaTiktok, FaPinterest } from 'react-icons/fa';
import { FaXTwitter, FaThreads } from 'react-icons/fa6';
import { SiTelegraph } from 'react-icons/si';
import { MdBusinessCenter, MdGroups, MdPersonOutline, MdAccessTime } from 'react-icons/md';
import tiktok1 from 'assets/img/screenshots/tiktok-1.png';
import tiktok2 from 'assets/img/screenshots/tiktok-2.png';
import tiktok3 from 'assets/img/screenshots/tiktok-3.png';
import tiktok4 from 'assets/img/screenshots/tiktok-4.png';
import tiktok5 from 'assets/img/screenshots/tiktok-5.png';
import tiktok6 from 'assets/img/screenshots/tiktok-6.png';

// Single source of truth cho integration pages. Landing platforms grid, /integrations
// overview, và /integrations/:slug detail page đều đọc từ đây.
// Khi add screenshot cho 1 platform → fill `screenshots` array → page tự render
// showcase thay cho "Coming soon" placeholder.
export const PLATFORMS = [
  {
    slug: 'tiktok',
    name: 'TikTok',
    icon: FaTiktok,
    color: '#010101',
    tagline: 'Short-form video publishing',
    description:
      'Creators authorize via TikTok Login Kit. Our backend exchanges the OAuth code (client secret stays server-side) and stores their profile. Users can browse their own videos via the Display API and publish new videos through the Content Posting API.',
    products: ['Login Kit', 'Content Posting API', 'Display API'],
    scopes: ['user.info.basic', 'user.info.profile', 'video.list', 'video.upload', 'video.publish'],
    compliance: [
      'Honors creator_info on every publish (allowed privacy levels, max_video_post_duration_sec, account-level interaction locks)',
      'Branded Content disclosure UI surfaces the required Promotional content / Paid partnership label requirements',
      'Access tokens encrypted at rest with AES-256-GCM',
    ],
    screenshots: [
      {
        src: tiktok1,
        step: '01',
        title: 'Connect via TikTok OAuth',
        desc:
          'Users authorize through the official TikTok consent screen using Login Kit (user.info.basic, user.info.profile). The client secret stays server-side.',
      },
      {
        src: tiktok2,
        step: '02',
        title: 'Connected accounts workspace',
        desc:
          'All connected TikTok accounts in a single dashboard, with status toggles, quick links to the public TikTok profile, and per-account actions.',
      },
      {
        src: tiktok3,
        step: '03',
        title: 'Account detail & profile sync',
        desc:
          'Profile data fetched from TikTok — display name, username, avatar, bio — with a re-sync button and a 24h post-count meter.',
      },
      {
        src: tiktok4,
        step: '04',
        title: 'Browse videos via Display API',
        desc:
          'Read-only video gallery using the video.list scope — cover image, title, view/like/comment/share counts, cursor pagination.',
      },
      {
        src: tiktok5,
        step: '05',
        title: 'Compose with creator_info gating',
        desc:
          'Post composer honors creator_info: allowed privacy levels, max video duration, account-level comment/duet/stitch locks, and Branded Content disclosure.',
      },
      {
        src: tiktok6,
        step: '06',
        title: 'Publish via Content Posting API',
        desc:
          'video.upload + video.publish — the post goes live on TikTok and the returned share_url is stored for verification.',
      },
    ],
    useCases: [
      {
        icon: MdBusinessCenter,
        title: 'Marketing agencies',
        desc:
          'Manage TikTok publishing for multiple client brands from a single workspace. Each connected TikTok account belongs to a client who authorized the agency via TikTok OAuth.',
      },
      {
        icon: MdGroups,
        title: 'In-house content teams',
        desc:
          'SMB marketing teams plan, schedule, and review TikTok video posts collaboratively, alongside Facebook, Instagram, and YouTube content from the same dashboard.',
      },
      {
        icon: MdPersonOutline,
        title: 'Solo creators',
        desc:
          'Independent creators schedule TikTok videos alongside 7 other social platforms from one place — no need to switch between native apps to publish.',
      },
      {
        icon: MdAccessTime,
        title: 'Timezone-aware scheduling',
        desc:
          'Schedule TikTok posts to fire at peak audience hours with Asia/Ho_Chi_Minh timezone support, auto-retry on transient failures, and a 24h post-count meter to respect TikTok rate limits.',
      },
    ],
    scopeJustifications: [
      {
        scope: 'user.info.basic',
        feature: 'OAuth callback & account identity',
        reason:
          'Required to retrieve open_id when the user authorizes our app. open_id is how we identify and look up the connected TikTok account in our database.',
      },
      {
        scope: 'user.info.profile',
        feature: 'Display creator identity in our UI',
        reason:
          'Used to show display_name, avatar_url, username, and bio_description on the account list, account detail, and post composer — so the user knows which TikTok account they are publishing to.',
      },
      {
        scope: 'video.list',
        feature: 'Read-only video gallery on account detail page',
        reason:
          'Lets the connected user browse their own published TikTok videos (cover image, title, view / like / comment / share counts) with cursor pagination, and verify that posts they published through our app are live on TikTok.',
      },
      {
        scope: 'video.upload',
        feature: 'Upload user video to the Content Posting API',
        reason:
          "First step of publishing — uploads the user's MP4 file to TikTok's video ingestion endpoint. Without this scope, no video can be sent to TikTok.",
      },
      {
        scope: 'video.publish',
        feature: 'Finalize the upload as a live TikTok post',
        reason:
          "Publishes the uploaded video with caption, privacy_level, disable_comment / disable_duet / disable_stitch, and Branded Content disclosure — making the post live on the creator's TikTok profile.",
      },
    ],
    testingSteps: [
      {
        step: 1,
        title: 'Sign in to the dashboard',
        desc:
          'Visit https://social-panel.adda.link/auth/sign-in and sign in with the reviewer test account provided in the App Review submission notes.',
      },
      {
        step: 2,
        title: 'Open the TikTok module',
        desc:
          'Click "Tài khoản TikTok" in the left sidebar (route /admin/tiktok). You will see the connected TikTok accounts list.',
      },
      {
        step: 3,
        title: 'Connect a TikTok account',
        desc:
          'Click "Kết nối TikTok" → on the next page click "Đăng nhập TikTok" → authorize via the official TikTok consent screen → set a display label → click "Hoàn tất kết nối".',
      },
      {
        step: 4,
        title: 'Browse your TikTok videos (Display API)',
        desc:
          'From the account list click the eye icon → on the detail page click "Xem video trên TikTok" to load the user\'s videos via video.list scope.',
      },
      {
        step: 5,
        title: 'Compose and publish a video',
        desc:
          'Navigate to "Bài viết TikTok" → "Tạo bài viết" → pick the connected account → upload an MP4 (or 1-35 photos) → set caption, privacy, toggles → choose "Đăng ngay" → submit.',
      },
      {
        step: 6,
        title: 'Verify on TikTok',
        desc:
          'Wait for the success toast. The new post appears in the list with a green "Đã đăng" badge and a permalink. Click the permalink to open the published video on tiktok.com.',
      },
    ],
    faq: [
      {
        q: 'Why does the app need video.publish?',
        a:
          'The core feature of Social Panel is scheduled, multi-platform publishing. video.publish lets us finalize an uploaded video as a live TikTok post on behalf of a creator who explicitly authorized our app for that purpose. Without it, our app cannot deliver its published value to users.',
      },
      {
        q: 'How is creator_info honored?',
        a:
          'Before showing the post composer, our backend calls /v2/post/publish/creator_info/query/. The UI then disables privacy options not in privacy_level_options, locks comment / duet / stitch toggles if the account already disabled them globally, and rejects videos longer than max_video_post_duration_sec at the upload step.',
      },
      {
        q: 'Where is the TikTok client_secret stored?',
        a:
          "Server-side only, in the backend's environment variables. The browser only receives the public client_key. The OAuth code-to-token exchange happens on our backend via POST /api/v1/tiktok-account/oauth-callback.",
      },
      {
        q: 'What happens if a user revokes the app on TikTok?',
        a:
          'Subsequent API calls return an authorization error which our backend captures and surfaces as a "Token expired" badge on the account detail page. The user is prompted to reconnect their TikTok account via OAuth to refresh tokens — no data loss in our system.',
      },
      {
        q: 'How are Branded Content disclosures handled?',
        a:
          'The composer exposes two toggles — Branded Content (paid partnership) and Your Brand (own product). Enabling either renders an in-UI notice with links to the Branded Content Policy and the TikTok Commercial Music Library, reminding the user that promotional posts must use commercial music. The flag is then sent to TikTok at publish time so the post is labeled correctly in feed.',
      },
      {
        q: 'How is access_token storage secured?',
        a:
          'Tokens are encrypted at rest with AES-256-GCM before being written to the database. The encryption key is held outside the database and rotated on a defined schedule. Tokens are never logged, never sent to the browser, and never shared between workspaces.',
      },
    ],
  },
  {
    slug: 'facebook',
    name: 'Facebook',
    icon: FaFacebook,
    color: '#1877F2',
    tagline: 'Page posts & scheduling',
    description:
      'Connect Facebook Pages via Facebook Login. Schedule and publish text, image, link, and video posts through the Facebook Graph API. Cron-driven scheduler handles delayed publishing in the workspace timezone.',
    products: ['Facebook Login', 'Graph API'],
    scopes: ['pages_show_list', 'pages_manage_posts', 'pages_read_engagement'],
    compliance: [
      'OAuth access tokens encrypted at rest with AES-256-GCM',
      'App secret stays server-side; no client_secret in browser',
    ],
    screenshots: [],
  },
  {
    slug: 'instagram',
    name: 'Instagram',
    icon: FaInstagram,
    color: '#E1306C',
    tagline: 'Feed posts, carousels & Reels',
    description:
      'Connect Instagram Business or Creator accounts via the Instagram API with Instagram Login (no Facebook Page required). View the connected profile and your published posts, then publish single images, carousels, and Reels — immediately or scheduled — through the Instagram Content Publishing API.',
    products: ['Instagram API with Instagram Login'],
    scopes: ['instagram_business_basic', 'instagram_business_content_publish'],
    compliance: [
      'Tokens encrypted at rest with AES-256-GCM; app secret stays server-side',
      'Media published via the container-based flow (/media → /media_publish)',
      'Disconnect revokes the stored token; deauthorize & data-deletion callbacks honored',
    ],
    screenshots: [],
    useCases: [
      {
        icon: MdBusinessCenter,
        title: 'Marketing agencies',
        desc:
          'Manage Instagram publishing for multiple client brands from one workspace. Each connected Instagram Business/Creator account belongs to a client who authorized the agency via Instagram OAuth.',
      },
      {
        icon: MdGroups,
        title: 'In-house content teams',
        desc:
          'SMB marketing teams plan, schedule, and review Instagram feed posts, carousels, and Reels collaboratively, alongside their other social content from the same dashboard.',
      },
      {
        icon: MdPersonOutline,
        title: 'Solo creators',
        desc:
          'Independent creators schedule Instagram posts alongside other social platforms from one place — no need to switch between native apps to publish.',
      },
      {
        icon: MdAccessTime,
        title: 'Timezone-aware scheduling',
        desc:
          'Schedule Instagram posts to fire at peak audience hours with Asia/Ho_Chi_Minh timezone support and auto-retry on transient failures.',
      },
    ],
    scopeJustifications: [
      {
        scope: 'instagram_business_basic',
        feature: 'Account identity, profile display & published-post history',
        reason:
          'Required to read the connected Instagram Business/Creator profile (user ID, username, name, avatar, account type, follower and media counts) and the list of posts the user has published (caption, media type, permalink, timestamp). We show this on the account list and account detail pages so the user knows which account they are managing and can verify posts published through our app.',
      },
      {
        scope: 'instagram_business_content_publish',
        feature: 'Publish images, carousels, and Reels to the user account',
        reason:
          'Lets the user publish content they compose in our dashboard. Our backend creates a media container (POST /{ig-user-id}/media) and publishes it (POST /{ig-user-id}/media_publish). Without this scope the app cannot deliver its core publishing feature.',
      },
    ],
    testingSteps: [
      {
        step: 1,
        title: 'Sign in to the dashboard',
        desc:
          'Visit https://social-panel.adda.link/auth/sign-in and sign in with the reviewer test account provided in the App Review submission notes.',
      },
      {
        step: 2,
        title: 'Open the Instagram module',
        desc:
          'Click "Instagram accounts" in the left sidebar (route /admin/instagram). You will see the connected accounts list.',
      },
      {
        step: 3,
        title: 'Connect an Instagram account',
        desc:
          'Click "Add" → on the next page click "Sign in with Instagram" → authorize on the official Instagram consent screen, granting instagram_business_basic and instagram_business_content_publish → click "Create".',
      },
      {
        step: 4,
        title: 'View profile & published posts (instagram_business_basic)',
        desc:
          'From the account list click the eye icon → the detail page reads the profile via GET /me and your published posts via GET /me/media. Click "Sync from Instagram" to refresh on demand.',
      },
      {
        step: 5,
        title: 'Compose and publish (instagram_business_content_publish)',
        desc:
          'Go to "Instagram posts" → "Create post" → pick the account → choose Single image, Carousel, or Reels → upload media → write a caption → choose "Publish now" → submit.',
      },
      {
        step: 6,
        title: 'Verify on Instagram',
        desc:
          'Wait for the success toast. The new post appears in the list with a "Published" badge and a permalink. Click it to open the live post on instagram.com.',
      },
    ],
    faq: [
      {
        q: 'Why does the app need instagram_business_content_publish?',
        a:
          'The core feature of Social Panel is scheduled, multi-platform publishing. This scope lets us publish images, carousels, and Reels that the user explicitly composes in our dashboard, on behalf of the Business/Creator account they authorized. Without it the app cannot deliver its published value.',
      },
      {
        q: 'Do you require a Facebook Page or Facebook Login?',
        a:
          'No. We use the Instagram API with Instagram Login (Business Login for Instagram), so the user authorizes directly with their Instagram Business/Creator account — no Facebook Page link and no Facebook Login are required.',
      },
      {
        q: 'Where is the Instagram app secret stored?',
        a:
          'Server-side only, in the backend environment. The browser only receives the public Instagram app ID. The OAuth code-to-token exchange happens on our backend against api.instagram.com/oauth/access_token.',
      },
      {
        q: 'What happens if a user revokes the app on Instagram?',
        a:
          'Meta calls our deauthorize callback; we immediately deactivate the integration and erase the stored access token. On a data-deletion request we purge the account, its tokens, and cached post metadata. Users can also disconnect anytime from our dashboard.',
      },
    ],
  },
  {
    slug: 'threads',
    name: 'Threads',
    icon: FaThreads,
    color: '#000000',
    tagline: 'Threads posts via Meta API',
    description:
      'Connect Threads accounts through the Meta OAuth consent flow on threads.net. Our backend exchanges the authorization code for a long-lived token (client secret stays server-side), then users can view their connected profile and published posts (threads_basic) and compose, schedule, or immediately publish text and media posts (threads_content_publish).',
    products: ['Threads API'],
    scopes: ['threads_basic', 'threads_content_publish'],
    compliance: [
      'Limited Use: Threads data is used only to provide the features shown here. We never sell or transfer it, never use it for advertising profiles, and never use it to train AI / ML models',
      'Long-lived access tokens are encrypted at rest with AES-256-GCM; the app secret stays server-side and is never sent to the browser',
      'A one-click "Disconnect" button permanently deletes the connected account, tokens and cached posts; we also honor Meta\'s Deauthorize and Data Deletion callbacks and fully purge data within 30 days',
      "We only publish content the user explicitly composes in our dashboard — we never auto-generate or auto-publish on the user's behalf",
      'Social Panel is an independent third-party tool and is not affiliated with, endorsed by, or sponsored by Meta Platforms, Inc.',
    ],
    screenshots: [],
    useCases: [
      {
        icon: MdBusinessCenter,
        title: 'Marketing agencies',
        desc:
          'Publish Threads posts for multiple client brands from one workspace. Each connected Threads account belongs to a client who authorized our app through the Meta consent screen.',
      },
      {
        icon: MdGroups,
        title: 'In-house content teams',
        desc:
          'Plan, schedule, and review Threads posts collaboratively, alongside Facebook, Instagram, YouTube and other content from the same dashboard.',
      },
      {
        icon: MdPersonOutline,
        title: 'Solo creators',
        desc:
          'Independent creators compose and schedule Threads posts next to 7 other social platforms — no need to switch between native apps to publish.',
      },
      {
        icon: MdAccessTime,
        title: 'Timezone-aware scheduling',
        desc:
          'Schedule Threads posts to fire at peak hours with Asia/Ho_Chi_Minh timezone support, auto-retry on transient failures, and a clear draft / scheduled / published / failed status pipeline.',
      },
    ],
    scopeJustifications: [
      {
        scope: 'threads_basic',
        feature: 'OAuth callback, profile display & published-posts view',
        reason:
          "Required to identify the connected account (user_id, username) after authorization and to display the user's own profile (name, avatar, biography, follower count) via GET /me and the list of posts they have published via GET /me/threads — so the user can confirm the correct account is linked and review existing posts before composing new ones.",
      },
      {
        scope: 'threads_content_publish',
        feature: "Publish composed posts to the user's Threads account",
        reason:
          'The core feature of Social Panel is scheduled, multi-platform publishing. This scope lets our backend create a media container (POST /me/threads) and publish it (POST /me/threads_publish) for content the user explicitly composed in our dashboard. Without it, the app cannot deliver its published value to users.',
      },
    ],
    testingSteps: [
      {
        step: 1,
        title: 'Sign in to the dashboard',
        desc:
          'Visit https://social-panel.adda.link/auth/sign-in and sign in with the reviewer test account provided in the App Review submission notes. The UI defaults to English.',
      },
      {
        step: 2,
        title: 'Open the Threads module',
        desc:
          'Click "Threads accounts" in the left sidebar (route /admin/threads). You will see the connected Threads accounts list.',
      },
      {
        step: 3,
        title: 'Connect a Threads account',
        desc:
          'Click "Add" → on the next page click "Sign in with Threads" → complete the Meta consent screen on threads.net (both threads_basic and threads_content_publish are listed) → click "Done" to save.',
      },
      {
        step: 4,
        title: 'View profile & published posts (threads_basic)',
        desc:
          'From the account list click the eye icon to open the detail page → click "Sync from Threads" to fetch the profile and the latest posts via GET /me and GET /me/threads → click "View all posts" to browse them.',
      },
      {
        step: 5,
        title: 'Compose and publish a post (threads_content_publish)',
        desc:
          'Navigate to "Threads posts" (/admin/threads-post) → "Create post" → pick the connected account → type up to 500 characters and optionally attach images/video → choose "Publish now" → submit.',
      },
      {
        step: 6,
        title: 'Verify on Threads',
        desc:
          'Wait for the success toast. The new post appears in the list with a green "Published" badge and a permalink. Click the permalink to open the live post on threads.net. You can also click "Disconnect" to permanently revoke access and delete stored data.',
      },
    ],
    faq: [
      {
        q: 'Why does the app need threads_content_publish?',
        a:
          'The core feature of Social Panel is scheduled, multi-platform publishing. threads_content_publish lets us publish a post (text, image, video, or carousel) the user explicitly composed in our dashboard to the Threads account they authorized. Without it, our app cannot deliver its published value to users.',
      },
      {
        q: 'Where is the Threads app secret / client_secret stored?',
        a:
          "Server-side only, in the backend's environment variables. The browser only carries the OAuth authorization code. The code-to-token exchange (including th_exchange_token for the long-lived token) happens on our backend via POST /api/v1/threads-account/oauth-callback. This is not a server-to-server app — the Meta login is fully user-facing.",
      },
      {
        q: 'What happens when a user disconnects or revokes the app on Threads?',
        a:
          'The "Disconnect" button immediately and permanently deletes the connected account, its tokens, and cached posts from our database. If the user removes the app from the Threads side instead, Meta calls our Deauthorize callback and we erase the stored tokens and deactivate the integration.',
      },
      {
        q: 'How can users delete their data?',
        a:
          'Users can disconnect any account in one click, or submit a data-deletion request. When Meta sends our Data Deletion callback (or the user deletes their Social Panel account), we permanently purge all Threads access tokens, user IDs, and cached profile/post metadata — within 30 days in all cases, except where retention is required by law.',
      },
      {
        q: 'How is access_token storage secured?',
        a:
          'Long-lived tokens are encrypted at rest with AES-256-GCM before being written to the database. The encryption key is held outside the database. Tokens are never logged, never sent to the browser, and never shared between workspaces.',
      },
    ],
  },
  {
    slug: 'youtube',
    name: 'YouTube',
    icon: FaYoutube,
    color: '#FF0000',
    tagline: 'Video uploads & metadata',
    description:
      'Connect YouTube channels via Google OAuth. Upload videos, set title, description, tags, privacy, and schedule via the YouTube Data API v3.',
    products: ['YouTube Data API v3'],
    scopes: ['youtube.upload', 'youtube.readonly'],
    compliance: ['Tokens encrypted at rest with AES-256-GCM', 'Resumable upload for large files'],
    screenshots: [],
  },
  {
    slug: 'x-twitter',
    name: 'X (Twitter)',
    icon: FaXTwitter,
    color: '#0F1419',
    tagline: 'Tweet scheduling & threads',
    description:
      'Connect X accounts via OAuth 2.0 with PKCE. Compose and schedule tweets, attach media, and chain replies into threads via the X API v2.',
    products: ['X API v2'],
    scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
    compliance: [
      'PKCE flow — no client_secret needed in browser',
      'Refresh tokens encrypted at rest with AES-256-GCM',
    ],
    screenshots: [],
  },
  {
    slug: 'pinterest',
    name: 'Pinterest',
    icon: FaPinterest,
    color: '#E60023',
    tagline: 'Pins & boards management',
    description:
      'Connect Pinterest accounts via OAuth. Create Pins on selected boards, set destination link and description, schedule pin publishing via the Pinterest API v5.',
    products: ['Pinterest API v5'],
    scopes: ['boards:read', 'pins:read', 'pins:write'],
    compliance: ['Tokens encrypted at rest with AES-256-GCM'],
    screenshots: [],
  },
  {
    slug: 'telegraph',
    name: 'Telegraph',
    icon: SiTelegraph,
    color: '#34ACE0',
    tagline: 'Long-form article publishing',
    description:
      'Publish long-form HTML / markdown articles to Telegraph instantly. Each workspace gets a Telegraph access token to manage its own articles.',
    products: ['Telegraph API'],
    scopes: ['access_token (per-account)'],
    compliance: ['Per-account access tokens encrypted at rest with AES-256-GCM'],
    screenshots: [],
  },
];

export const getPlatformBySlug = slug => PLATFORMS.find(p => p.slug === slug);
